import { useTranslation } from 'react-i18next';
import { ChatBubbleIcon, HashIcon, UsersIcon } from '@/components/icons';
import { HeroFadeIn } from '@/heroui/motion/HeroFadeIn';
import type { SavedChannelKind } from '@/lib/savedMessages';

/**
 * HeroUI-skinned empty channel state. Rendered instead of {@link EmptyChannel}
 * when the app skin is 'heroui'. Same copy and structure — built from HeroUI
 * primitives (Card / Chip / Kbd) so it themes through HeroUI's light/dark
 * palette. This is the first proof slice of the HeroUI skin.
 */
export function EmptyChannelHero(props: { name: string; kind: SavedChannelKind; isVault?: boolean }) {
  const { t } = useTranslation();
  if (props.isVault) {
    return (
      <div className="grid h-full place-items-center px-6 py-12">
        <HeroFadeIn>
          <p className="max-w-sm text-center text-sm leading-6 text-muted">{t('ui2.emptyHero.startVault')}</p>
        </HeroFadeIn>
      </div>
    );
  }
  const isRoom = props.kind === 'TEXT' || props.kind === 'ANNOUNCE';
  return (
    <div className="grid h-full place-items-center px-6 py-12">
      <HeroFadeIn>
        <div className="flex max-w-sm flex-col items-center text-center">
          <div className="grid size-14 place-items-center rounded-2xl bg-primary/10 text-primary">
            {isRoom ? (
              <HashIcon className="size-6" />
            ) : props.kind === 'GROUP_DM' ? (
              <UsersIcon className="size-6" />
            ) : (
              <ChatBubbleIcon className="size-6" />
            )}
          </div>
          <p className="mt-5 max-w-full break-words text-2xl font-semibold leading-tight tracking-tight text-foreground">
            {t('ui2.emptyHero.emptyTitle')}
          </p>
          <p className="mt-2 text-sm leading-6 text-default-500">
            {isRoom
                ? t('ui2.emptyHero.startChannel')
                : props.kind === 'GROUP_DM'
                  ? t('ui2.emptyHero.startGroup')
                  : t('ui2.emptyHero.startDirect')}
          </p>
        </div>
      </HeroFadeIn>
    </div>
  );
}
